import {
    useReducer, useEffect, useCallback, useRef
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import store from 'store/Store';
import bazaarVoiceApi from 'services/api/thirdparty/BazaarVoice';
import userUtils from 'utils/User';
import biUtils from 'utils/BiProfile';
import UI from 'utils/UI';
import paginationUtils from 'utils/Pagination';
import ProductActions from 'actions/ProductActions';
import anaConsts from 'analytics/constants';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import BPUtils from 'utils/BeautyPreferences';
import ProductUtils from 'utils/product';
import { FILTER_CAT_IDS, FILTER_IDS, CATEGORY_SPECIFIC_FILTER_CONFIGS } from 'constants/reviewFilters';

const { findProductRootParentCategoryId } = ProductUtils;
const PAGE_SIZE = 6;

const initialState = {
    carouselsReady: false,
    helpfulReviews: null,
    currentPage: 1,
    isSearchPerformed: false,
    reviewsWithImage: [],
    reviewSelectedPhotoId: null,
    selectedReviewId: null,
    showProsAndCons: true,
    sentimentLoading: false,
    showComponent: false,
    hasNewFilter: false,
    hasReviews: false,
    reviews: undefined,
    totalReviews: undefined,
    filtersApplied: undefined,
    reviewStatistics: undefined,
    initialTotalReviews: undefined,
    user: null,
    categoryId: null,
    sentimentReviews: false,
    sentimentType: null,
    searchKeyword: '',
    bvFilters: []
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'SET_FETCH_RESULT':
            return { ...state, ...action.payload };
        case 'SET_SEARCH_RESULT':
            return { ...state, ...action.payload };
        case 'SET_PAGE':
            return { ...state, currentPage: action.value };
        case 'SET_USER':
            return { ...state, user: action.user };
        case 'SET_CATEGORY':
            return { ...state, categoryId: action.value };
        case 'SET_SENTIMENT_LOADING':
            return { ...state, sentimentLoading: action.value, currentPage: action.page || state.currentPage };
        case 'SET_HIGHLIGHTED':
            return { ...state, ...action.payload };
        case 'CLEAR_SENTIMENT':
            return { ...state, sentimentLoading: false, sentimentReviews: false };
        case 'SET_SEARCH_MODE':
            return { ...state, isSearchPerformed: false };
        case 'HIDE_COMPONENT':
            return { ...state, showComponent: false };
        case 'SET_IMAGE_DATA':
            return { ...state, ...action.payload };
        case 'RESET':
            return { ...initialState, ...action.override };
        default:
            return state;
    }
};

export const useReviewsData = ({
    productId,
    currentProduct,
    selectedSentiment,
    language,
    showPrsModule,
    incomingReviewStatistics,
    incomingInitialTotalReviews,
    showAddDropDownReviewSection,
    loadHighlightedReviewsAction,
    reviewsToShow: _reviewsToShow
}) => {
    const [state, localDispatch] = useReducer(reducer, initialState);
    const dispatch = useDispatch();
    const highlightedReviewsFromStore = useSelector(reduxState => reduxState.page?.product?.highlightedReviews);
    const stateRef = useRef(state);
    stateRef.current = state;
    const prevProductIdRef = useRef(productId);
    const prevSentimentRef = useRef(selectedSentiment);

    const normalizeHighlightedReviews = useCallback(
        reviews =>
            reviews.map(review => ({
                ...review,
                reviewText: review.text,
                userNickname: review.author,
                badges: { ...review.badges, incentivizedReview: review.incentivized }
            })),
        []
    );

    const loadHighlightedReviews = useCallback(
        data => {
            localDispatch({
                type: 'SET_HIGHLIGHTED',
                payload: {
                    reviews: normalizeHighlightedReviews(data.reviews),
                    totalReviews: data.count,
                    sentimentReviews: true,
                    sentimentType: data.proConType,
                    sentimentLoading: false,
                    hasReviews: Array.isArray(data.reviews) && data.reviews.length > 0
                }
            });
        },
        [normalizeHighlightedReviews]
    );

    const fetchData = useCallback(
        (filters, isFirstCall, currentPage = 1) => {
            const cur = stateRef.current;
            const refinementsByCategory = BPUtils.getMasterListRefinementsByCategory(cur.categoryId);
            const filtersToApply = { ...(filters || cur.filtersApplied) };
            const hasNewFilter = JSON.stringify(filtersToApply) !== JSON.stringify(cur.filtersApplied);
            const offset = hasNewFilter ? 0 : (currentPage - 1) * PAGE_SIZE;

            bazaarVoiceApi
                .getReviewsAndStatsWithConfig(currentProduct.reviewFilters, productId, PAGE_SIZE, filtersToApply, offset, refinementsByCategory)
                .then(data => {
                    if (data.apiFailed) {
                        localDispatch({ type: 'HIDE_COMPONENT' });

                        return;
                    }

                    if (!data.results) {
                        return;
                    }

                    // `cur` was captured before getReviewsAndStatsWithConfig resolved and may be stale; read fresh state.
                    const now = stateRef.current;

                    if (now.sentimentReviews) {
                        localDispatch({
                            type: 'SET_FETCH_RESULT',
                            payload: {
                                showComponent: true,
                                reviewStatistics: isFirstCall ? data.reviewStatistics : now.reviewStatistics,
                                filtersApplied: filtersToApply,
                                initialTotalReviews: isFirstCall ? data.totalResults : now.initialTotalReviews
                            }
                        });

                        return;
                    }

                    localDispatch({
                        type: 'SET_FETCH_RESULT',
                        payload: {
                            showComponent: true,
                            hasNewFilter: isFirstCall ? false : hasNewFilter,
                            hasReviews: Array.isArray(data.results) && data.results.length > 0,
                            isSearchPerformed: false,
                            reviews: data.results,
                            totalReviews: data.totalResults,
                            filtersApplied: filtersToApply,
                            currentPage,
                            reviewStatistics: isFirstCall ? data.reviewStatistics : now.reviewStatistics,
                            initialTotalReviews: isFirstCall ? data.totalResults : now.initialTotalReviews
                        }
                    });
                })
                .catch(error => error.apiFailed && localDispatch({ type: 'HIDE_COMPONENT' }));
        },
        [productId, currentProduct]
    );

    const loadInitialReviews = useCallback(() => {
        const highlightedReviews = store.getState().page.product?.highlightedReviews;

        if (highlightedReviews?.reviews?.length > 0) {
            loadHighlightedReviews(highlightedReviews);
        } else {
            let defaultFilters = {};
            const { reviewFilters } = currentProduct;

            if (reviewFilters) {
                const sortFilter = reviewFilters.find(filter => filter.id === 'sort');

                if (sortFilter?.options) {
                    const submissionTimeDesc = sortFilter.options.find(x => x.bvName === 'SubmissionTime' && x.bvValue === 'desc');

                    if (submissionTimeDesc?.value) {
                        defaultFilters = { sort: [submissionTimeDesc.value] };
                    }
                }
            }

            fetchData(defaultFilters, true);
        }
    }, [fetchData, loadHighlightedReviews, currentProduct]);

    const initReviews = useCallback(
        ({
            incomingReviewStatistics: statsArg,
            incomingInitialTotalReviews: totalArg,
            showAddDropDownReviewSection: showDropdownArg,
            reset = false
        }) => {
            const shouldShowDropdown = showDropdownArg;
            const hasReviewStats = !!statsArg;
            const hasTotalReviews = totalArg !== null && totalArg !== undefined;

            if (shouldShowDropdown && hasReviewStats && hasTotalReviews) {
                const baseState = {
                    reviewStatistics: statsArg,
                    initialTotalReviews: totalArg,
                    showComponent: true
                };

                if (reset) {
                    Object.assign(baseState, {
                        currentPage: 1,
                        isSearchPerformed: false,
                        filtersApplied: undefined,
                        reviews: undefined,
                        totalReviews: undefined
                    });
                }

                localDispatch({ type: 'SET_FETCH_RESULT', payload: baseState });
                const shouldFetch = reset ? totalArg > 0 : !!totalArg;

                if (shouldFetch) {
                    setTimeout(() => fetchData({}, false, 1), 0);
                }
            } else {
                loadInitialReviews();
            }
        },
        [fetchData, loadInitialReviews]
    );

    const filterReviews = useCallback(
        filters => {
            if (!stateRef.current.isSearchPerformed) {
                fetchData(filters);
            }
        },
        [fetchData]
    );

    const filterSearchReviews = useCallback((searchData, isFirstCall = true, currentPage = 1) => {
        const { productId: pid, keyword } = searchData;
        const offset = isFirstCall ? 0 : (currentPage - 1) * PAGE_SIZE;
        bazaarVoiceApi
            .getSearchReviews(pid, PAGE_SIZE, keyword, offset)
            .then(data => {
                if (data.apiFailed) {
                    localDispatch({ type: 'HIDE_COMPONENT' });

                    return;
                }

                if (!data.results) {
                    return;
                }

                const actionLink = 'reviews:ratings&reviews:search';
                const eventType = data.totalResults > 0 ? anaConsts.Event.EVENT_80 : anaConsts.Event.EVENT_81;
                pdpAnalytics.trackLinkClick({ eventStrings: eventType, searchAnswersKeyword: keyword, actionInfo: actionLink, linkName: actionLink });
                localDispatch({
                    type: 'SET_SEARCH_RESULT',
                    payload: {
                        isSearchPerformed: true,
                        searchKeyword: keyword,
                        showComponent: true,
                        hasNewFilter: false,
                        hasReviews: Array.isArray(data.results) && data.results.length > 0,
                        reviews: data.results,
                        totalReviews: data.totalResults,
                        currentPage
                    }
                });
                UI.scrollTo({ elementId: 'custom_filters' });
            })
            .catch(error => error.apiFailed && localDispatch({ type: 'HIDE_COMPONENT' }));
    }, []);

    const handlePageClick = useCallback(
        (pageIndex, buttonType) => {
            const cur = stateRef.current;

            if (cur.isSearchPerformed) {
                filterSearchReviews({ keyword: cur.searchKeyword, productId, type: 'REVIEW_SEARCH_APPLIED' }, false, pageIndex);
            } else if (selectedSentiment) {
                localDispatch({ type: 'SET_PAGE', value: pageIndex });
                dispatch(
                    loadHighlightedReviewsAction({
                        sentiment: selectedSentiment,
                        type: cur.sentimentType,
                        productId,
                        language,
                        limit: PAGE_SIZE,
                        page: pageIndex - 1,
                        sentimentSource: showPrsModule ? 'seph_sentiment' : undefined
                    })
                );
            } else {
                fetchData(null, false, pageIndex);
            }

            paginationUtils.sendAnalytics('ratings&reviews', pageIndex, buttonType);
        },
        [productId, selectedSentiment, language, showPrsModule, fetchData, filterSearchReviews, dispatch, loadHighlightedReviewsAction]
    );

    const handleReviewImageOnclick = useCallback(
        (selectedReviewId, reviewSelectedPhotoId) => {
            const cur = stateRef.current;

            if (cur.reviewsWithImage.length) {
                localDispatch({ type: 'SET_IMAGE_DATA', payload: { reviewSelectedPhotoId, selectedReviewId } });
            } else {
                bazaarVoiceApi.getReviewsWithImage(productId).then(data => {
                    if (data) {
                        localDispatch({
                            type: 'SET_IMAGE_DATA',
                            payload: { reviewsWithImage: data.results, reviewSelectedPhotoId, selectedReviewId }
                        });
                    }
                });
            }
        },
        [productId]
    );

    const getRemappedReviewFilters = useCallback(() => {
        const { reviewFilters } = currentProduct;
        const { categoryId } = stateRef.current;
        const refinements = BPUtils.getMasterListRefinementsByCategory(categoryId);
        const remappedFilters = reviewFilters
            .filter(
                filter =>
                    !(
                        ([FILTER_CAT_IDS.HAIR, FILTER_CAT_IDS.FRAGRANCE, FILTER_CAT_IDS.TOOLS].includes(categoryId) &&
                            filter.id === FILTER_IDS.AGE_RANGE) ||
                        (categoryId === FILTER_CAT_IDS.SKIN_CARE && filter.id === FILTER_IDS.SKIN_TONE)
                    )
            )
            .map(filter => {
                const filterId = filter.id === FILTER_IDS.HAIR_DESCRIBLE ? FILTER_IDS.HAIR_TYPE : filter.id;
                const masterListFilter = refinements.find(item => item.key === filterId);

                if (masterListFilter) {
                    return {
                        ...filter,
                        id: filterId,
                        options: BPUtils.sortArrayObjectsByOrderId(
                            masterListFilter.items
                                .filter(item => !item.key.includes('notSure') && item.visableInBP)
                                .map(item => ({
                                    bvName: null,
                                    bvValue: item.key,
                                    value: item.value,
                                    img: item.image,
                                    hideIcon: filterId === FILTER_IDS.AGE_RANGE,
                                    orderId: item.orderId
                                }))
                        )
                    };
                }

                return filter;
            });
        CATEGORY_SPECIFIC_FILTER_CONFIGS.forEach(config => {
            if (categoryId === config.categoryId) {
                const filterIndex = remappedFilters.findIndex(f => f.id === config.filterId);
                const afterIndex = remappedFilters.findIndex(f => f.id === config.afterFilterId);
                const masterFilter = refinements.find(item => item.key === config.filterId);

                if (filterIndex === -1 && masterFilter && afterIndex > -1) {
                    remappedFilters.splice(afterIndex + 1, 0, {
                        id: config.filterId,
                        label: masterFilter.value,
                        type: 'multiSelect',
                        options: BPUtils.sortArrayObjectsByOrderId(
                            masterFilter.items
                                .filter(item => item.filterable && !config.excludeOptions.some(exclude => item.key.includes(exclude)))
                                .map(item => ({
                                    bvName: null,
                                    bvValue: item.key,
                                    value: item.value,
                                    img: item.image,
                                    orderId: item.orderId
                                }))
                        )
                    });
                }
            }
        });

        return remappedFilters;
    }, [currentProduct]);

    useEffect(() => {
        localDispatch({ type: 'SET_CATEGORY', value: findProductRootParentCategoryId(currentProduct) });
        store.watchAction(ProductActions.TYPES.REVIEW_FILTERS_APPLIED, data => filterReviews(data.filters));
        store.watchAction(ProductActions.TYPES.REVIEW_SEARCH_APPLIED, data => filterSearchReviews(data));
    }, []);

    useEffect(() => {
        const isAnonymous = userUtils.isAnonymous();
        localDispatch({ type: 'SET_USER', user: { isBI: userUtils.isBI(), isAnonymous, biUserInfo: biUtils.getBiProfileInfo() } });
    }, []);

    useEffect(() => {
        if (highlightedReviewsFromStore) {
            if (highlightedReviewsFromStore?.reviews?.length > 0) {
                loadHighlightedReviews(highlightedReviewsFromStore);
            }
        } else {
            localDispatch({ type: 'SET_SENTIMENT_LOADING', value: false });

            if (stateRef.current.sentimentReviews) {
                loadInitialReviews();
                localDispatch({ type: 'CLEAR_SENTIMENT' });
            }
        }
    }, [highlightedReviewsFromStore]);

    useEffect(() => {
        if (prevProductIdRef.current !== productId) {
            prevProductIdRef.current = productId;
            localDispatch({ type: 'RESET', override: {} });
            initReviews({
                incomingReviewStatistics,
                incomingInitialTotalReviews,
                showAddDropDownReviewSection,
                reset: true
            });
        }
    }, [productId, initReviews]);

    useEffect(() => {
        if (prevSentimentRef.current !== selectedSentiment) {
            prevSentimentRef.current = selectedSentiment;
            localDispatch({ type: 'SET_SENTIMENT_LOADING', value: !!selectedSentiment, page: 1 });
        }
    }, [selectedSentiment]);

    return {
        state,
        localDispatch,
        fetchData,
        loadInitialReviews,
        initReviews,
        filterReviews,
        handlePageClick,
        handleReviewImageOnclick,
        getRemappedReviewFilters,
        PAGE_SIZE
    };
};
